import React, { useState } from 'react';
import { useCasesData, roiBenefitsData } from '../data/useCases';
import { Clock, TrendingDown, TrendingUp, Zap, Target, CheckCircle, DollarSign, ArrowRight, Calendar } from 'lucide-react';
import { Button } from './ui/button';

const iconMap = {
  clock: Clock,
  'trending-down': TrendingDown,
  'trending-up': TrendingUp,
  zap: Zap,
  target: Target,
  'check-circle': CheckCircle,
  'dollar-sign': DollarSign
};

const UseCases = () => {
  const [selectedCase, setSelectedCase] = useState(null);

  const scrollToContact = () => {
    const element = document.getElementById('contacto');
    if (element) {
      element.scrollIntoView({ behavior: 'smooth' });
    }
  };

  return (
    <section className="py-20 bg-gradient-to-b from-white to-gray-50">
      <div className="container mx-auto px-4">
        {/* Header */}
        <div className="text-center mb-16">
          <h2 className="text-4xl md:text-5xl font-bold text-[#013139] mb-4">
            Casos de Éxito Reales
          </h2>
          <p className="text-xl text-gray-600 max-w-3xl mx-auto">
            Descubre cómo empresas como la tuya están transformando su operativa con agentes de IA trabajando 24/7
          </p>
        </div>

        {/* ROI Impact Section */}
        <div className="mb-20 bg-gradient-to-br from-[#013139] to-[#024F5B] rounded-3xl p-8 md:p-12 text-white">
          <h3 className="text-3xl font-bold mb-4 text-center">{roiBenefitsData.title}</h3>
          <p className="text-gray-200 text-center mb-12 max-w-2xl mx-auto">{roiBenefitsData.subtitle}</p>
          
          {/* Stats Grid */}
          <div className="grid md:grid-cols-4 gap-6 mb-12">
            {roiBenefitsData.stats.map((stat, index) => {
              const IconComponent = iconMap[stat.icon];
              return (
                <div key={index} className="bg-white/10 backdrop-blur-md rounded-xl p-6 text-center border border-white/20 hover:bg-white/20 transition-all">
                  <div className="flex justify-center mb-3">
                    <div className="w-12 h-12 bg-[#00E5A3] rounded-lg flex items-center justify-center">
                      <IconComponent className="text-[#013139]" size={24} />
                    </div>
                  </div>
                  <div className="text-4xl font-bold text-[#00E5A3] mb-2">{stat.metric}</div>
                  <div className="text-sm text-gray-200">{stat.label}</div>
                </div>
              );
            })}
          </div>

          {/* Advantages */}
          <div className="grid md:grid-cols-2 gap-6">
            {roiBenefitsData.advantages.map((advantage, index) => {
              const IconComponent = iconMap[advantage.icon];
              return (
                <div key={index} className="flex gap-4">
                  <div className="flex-shrink-0">
                    <div className="w-10 h-10 bg-[#00E5A3] rounded-lg flex items-center justify-center">
                      <IconComponent className="text-[#013139]" size={20} />
                    </div>
                  </div>
                  <div>
                    <h4 className="font-bold text-lg mb-2">{advantage.title}</h4>
                    <p className="text-gray-200 text-sm">{advantage.description}</p>
                  </div>
                </div>
              );
            })}
          </div>
        </div>

        {/* Use Cases Cards */}
        <div className="grid md:grid-cols-3 gap-8 mb-12">
          {useCasesData.map((useCase) => (
            <div
              key={useCase.id}
              className="bg-white rounded-2xl shadow-lg hover:shadow-2xl transition-all duration-300 overflow-hidden border border-gray-100 cursor-pointer group"
              onClick={() => setSelectedCase(useCase)}
            >
              {/* Image */}
              <div className="relative h-48 overflow-hidden">
                <img
                  src={useCase.image}
                  alt={useCase.title}
                  className="w-full h-full object-cover group-hover:scale-110 transition-transform duration-500"
                />
                <div className="absolute inset-0 bg-gradient-to-t from-[#013139]/80 to-transparent" />
                <div className="absolute bottom-4 left-4">
                  <span className="bg-[#00E5A3] text-[#013139] text-xs font-bold px-3 py-1 rounded-full">
                    {useCase.industry}
                  </span>
                </div>
              </div>

              {/* Content */}
              <div className="p-6">
                <div className="flex items-center gap-2 mb-3 text-sm text-gray-500">
                  <Calendar size={14} />
                  <span>{useCase.date}</span>
                </div>
                
                <h3 className="text-xl font-bold text-[#013139] mb-3 line-clamp-2">
                  {useCase.title}
                </h3>

                <p className="text-gray-600 mb-4 text-sm line-clamp-3">
                  {useCase.problem}
                </p>

                {/* Quick Metrics */}
                <div className="grid grid-cols-2 gap-3 mb-4">
                  <div className="bg-[#00E5A3]/10 rounded-lg p-2 text-center">
                    <div className="text-lg font-bold text-[#013139]">{useCase.metrics.timeSaved}</div>
                    <div className="text-[10px] text-gray-600">Ahorro tiempo</div>
                  </div>
                  <div className="bg-[#00E5A3]/10 rounded-lg p-2 text-center">
                    <div className="text-lg font-bold text-[#013139]">{useCase.metrics.roi}</div>
                    <div className="text-[10px] text-gray-600">ROI</div>
                  </div>
                </div>

                <button className="text-[#00E5A3] hover:text-[#00C497] font-semibold text-sm flex items-center gap-2 group">
                  Ver caso completo
                  <ArrowRight size={16} className="group-hover:translate-x-1 transition-transform" />
                </button>
              </div>
            </div>
          ))}
        </div>

        {/* CTA */}
        <div className="text-center bg-gradient-to-r from-gray-50 to-white p-12 rounded-3xl border border-gray-200">
          <h3 className="text-3xl font-bold text-[#013139] mb-4">
            ¿Quieres resultados similares en tu negocio?
          </h3>
          <p className="text-xl text-gray-600 mb-8 max-w-2xl mx-auto">
            Analicemos tu caso y calculemos el ROI específico para tu empresa
          </p>
          <Button
            onClick={scrollToContact}
            className="bg-[#00E5A3] hover:bg-[#00C497] text-[#013139] font-bold text-lg px-8 py-6"
          >
            Solicita tu análisis gratuito
            <ArrowRight className="ml-2" size={20} />
          </Button>
        </div>
      </div>

      {/* Modal para caso detallado */}
      {selectedCase && (
        <div className="fixed inset-0 bg-black/50 z-50 flex items-center justify-center p-4" onClick={() => setSelectedCase(null)}>
          <div className="bg-white rounded-3xl max-w-4xl w-full max-h-[90vh] overflow-y-auto" onClick={(e) => e.stopPropagation()}>
            <div className="sticky top-0 bg-white border-b border-gray-200 p-6 flex justify-between items-center z-10">
              <h2 className="text-2xl font-bold text-[#013139]">{selectedCase.title}</h2>
              <button
                onClick={() => setSelectedCase(null)}
                className="text-gray-500 hover:text-[#013139] text-2xl font-bold"
              >
                ×
              </button>
            </div>

            <div className="p-8">
              {/* Image */}
              <img
                src={selectedCase.image}
                alt={selectedCase.title}
                className="w-full h-64 object-cover rounded-2xl mb-8"
              />

              {/* Client Info */}
              <div className="mb-8">
                <span className="bg-[#00E5A3] text-[#013139] text-sm font-bold px-4 py-2 rounded-full">
                  {selectedCase.industry}
                </span>
                <span className="ml-3 text-gray-600">Cliente: {selectedCase.client}</span>
              </div>

              {/* Problem & Solution */}
              <div className="grid md:grid-cols-2 gap-8 mb-8">
                <div>
                  <h3 className="text-xl font-bold text-[#013139] mb-4">{selectedCase.problemTitle}</h3>
                  <p className="text-gray-700 leading-relaxed">{selectedCase.problem}</p>
                </div>
                <div>
                  <h3 className="text-xl font-bold text-[#013139] mb-4">{selectedCase.solutionTitle}</h3>
                  <p className="text-gray-700 leading-relaxed">{selectedCase.solution}</p>
                </div>
              </div>

              {/* Metrics */}
              <div className="bg-gradient-to-br from-[#013139] to-[#024F5B] rounded-2xl p-8 mb-8 text-white">
                <h3 className="text-2xl font-bold mb-6 text-center">Resultados Medibles</h3>
                <div className="grid grid-cols-2 md:grid-cols-5 gap-4">
                  <div className="text-center">
                    <div className="text-3xl font-bold text-[#00E5A3] mb-2">{selectedCase.metrics.timeSaved}</div>
                    <div className="text-sm text-gray-200">Tiempo Ahorrado</div>
                  </div>
                  <div className="text-center">
                    <div className="text-3xl font-bold text-[#00E5A3] mb-2">{selectedCase.metrics.costReduction}</div>
                    <div className="text-sm text-gray-200">Reducción Costes</div>
                  </div>
                  <div className="text-center">
                    <div className="text-3xl font-bold text-[#00E5A3] mb-2">{selectedCase.metrics.productivityIncrease}</div>
                    <div className="text-sm text-gray-200">Aumento Productividad</div>
                  </div>
                  <div className="text-center">
                    <div className="text-3xl font-bold text-[#00E5A3] mb-2">{selectedCase.metrics.roi}</div>
                    <div className="text-sm text-gray-200">ROI</div>
                  </div>
                  <div className="text-center">
                    <div className="text-3xl font-bold text-[#00E5A3] mb-2">{selectedCase.metrics.paybackPeriod}</div>
                    <div className="text-sm text-gray-200">Recuperación</div>
                  </div>
                </div>
              </div>

              {/* Before/After */}
              <div className="grid md:grid-cols-2 gap-8 mb-8">
                <div>
                  <h3 className="text-xl font-bold text-[#013139] mb-4">❌ Antes de la Automatización</h3>
                  <div className="space-y-3">
                    {selectedCase.impactBefore.map((item, index) => (
                      <div key={index} className="flex justify-between p-3 bg-red-50 rounded-lg">
                        <span className="text-gray-700">{item.metric}</span>
                        <span className="font-bold text-red-600">{item.value}</span>
                      </div>
                    ))}
                  </div>
                </div>
                <div>
                  <h3 className="text-xl font-bold text-[#013139] mb-4">✅ Después de la Automatización</h3>
                  <div className="space-y-3">
                    {selectedCase.impactAfter.map((item, index) => (
                      <div key={index} className="flex justify-between p-3 bg-green-50 rounded-lg">
                        <span className="text-gray-700">{item.metric}</span>
                        <span className="font-bold text-green-600">{item.value}</span>
                      </div>
                    ))}
                  </div>
                </div>
              </div>

              {/* Features */}
              <div className="mb-8">
                <h3 className="text-xl font-bold text-[#013139] mb-4">Funcionalidades Implementadas</h3>
                <ul className="grid md:grid-cols-2 gap-3">
                  {selectedCase.features.map((feature, index) => (
                    <li key={index} className="flex items-start gap-2">
                      <CheckCircle className="text-[#00E5A3] flex-shrink-0 mt-0.5" size={18} />
                      <span className="text-gray-700">{feature}</span>
                    </li>
                  ))}
                </ul>
              </div>

              {/* Testimonial */}
              <div className="bg-gradient-to-r from-gray-50 to-white p-8 rounded-2xl border-l-4 border-[#00E5A3]">
                <p className="text-xl text-gray-700 italic mb-4">"{selectedCase.testimonial.text}"</p>
                <div>
                  <p className="font-bold text-[#013139]">{selectedCase.testimonial.author}</p>
                  <p className="text-gray-600">{selectedCase.testimonial.role}</p>
                </div>
              </div>
            </div>
          </div>
        </div>
      )}
    </section>
  );
};

export default UseCases;
